import React from 'react';
import styled from 'styled-components';

// Styled Components
const FormSection = styled.section`
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f8f9fa;
    color: var(--primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--dark);
  }
  
  .form-text {
    font-size: 0.85rem;
    color: var(--gray);
    margin-top: 0.25rem;
  }
  
  input, select, textarea {
    border: 2px solid ${props => props.hasError ? 'var(--primary)' : '#f0f0f0'};
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: ${props => props.hasError ? 'var(--primary)' : 'var(--primary-light)'};
      box-shadow: 0 0 0 0.25rem ${props => props.hasError ? 'rgba(233, 64, 87, 0.25)' : 'rgba(233, 64, 87, 0.25)'};
    }
  }
  
  .error-message {
    color: var(--primary);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.1)' : '#f8f9fa'};
  border: 1px solid ${props => props.checked ? 'var(--primary)' : '#e9ecef'};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.15)' : '#e9ecef'};
  }
  
  input {
    margin-right: 0.5rem;
  }
`;

const FeedingSection = ({ formData, handleChange, errors }) => {
  return (
    <FormSection>
      <FormGroup hasError={!!errors.feedingType}>
        <label htmlFor="feedingType">Feeding Type</label>
        <select
          className="form-select"
          id="feedingType"
          name="feedingType"
          value={formData.feedingType}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Exclusively breastfed">Exclusively breastfed</option>
          <option value="Formula fed">Formula fed</option>
          <option value="Combination fed">Combination fed</option>
          <option value="Solids introduced">Solids introduced</option>
        </select>
        {errors.feedingType && <div className="error-message">{errors.feedingType}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.overnightFeeds}>
        <label htmlFor="overnightFeeds">Number of Overnight Feeds</label>
        <select
          className="form-select"
          id="overnightFeeds"
          name="overnightFeeds"
          value={formData.overnightFeeds}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="0">0 (no overnight feeds)</option>
          <option value="1">1 feed per night</option>
          <option value="2">2 feeds per night</option>
          <option value="3">3 feeds per night</option>
          <option value="4+">4+ feeds per night</option>
        </select>
        {errors.overnightFeeds && <div className="error-message">{errors.overnightFeeds}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.fallsAsleepDuringFeeds}>
        <label htmlFor="fallsAsleepDuringFeeds">Does baby fall asleep during feeds?</label>
        <select
          className="form-select"
          id="fallsAsleepDuringFeeds"
          name="fallsAsleepDuringFeeds"
          value={formData.fallsAsleepDuringFeeds}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Sometimes">Sometimes</option>
        </select>
        {errors.fallsAsleepDuringFeeds && <div className="error-message">{errors.fallsAsleepDuringFeeds}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.wakesMultipleTimes}>
        <label htmlFor="wakesMultipleTimes">Does your baby wake multiple times overnight?</label>
        <select
          className="form-select"
          id="wakesMultipleTimes"
          name="wakesMultipleTimes"
          value={formData.wakesMultipleTimes}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Sometimes">Sometimes</option>
        </select>
        {errors.wakesMultipleTimes && <div className="error-message">{errors.wakesMultipleTimes}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.responseToWakes}>
        <label>How do you usually respond to wakes? (select all that apply)</label>
        <CheckboxGroup>
          <CheckboxLabel checked={formData.responseToWakes.feed}>
            <input
              type="checkbox"
              name="responseToWakes.feed"
              checked={formData.responseToWakes.feed}
              onChange={handleChange}
            />
            Feed
          </CheckboxLabel>
          <CheckboxLabel checked={formData.responseToWakes.rock}>
            <input
              type="checkbox"
              name="responseToWakes.rock"
              checked={formData.responseToWakes.rock}
              onChange={handleChange}
            />
            Rock
          </CheckboxLabel>
          <CheckboxLabel checked={formData.responseToWakes.dummy}>
            <input
              type="checkbox"
              name="responseToWakes.dummy"
              checked={formData.responseToWakes.dummy}
              onChange={handleChange}
            />
            Dummy
          </CheckboxLabel>
          <CheckboxLabel checked={formData.responseToWakes.cuddle}>
            <input
              type="checkbox"
              name="responseToWakes.cuddle"
              checked={formData.responseToWakes.cuddle}
              onChange={handleChange}
            />
            Cuddle
          </CheckboxLabel>
          <CheckboxLabel checked={formData.responseToWakes.wait}>
            <input
              type="checkbox"
              name="responseToWakes.wait"
              checked={formData.responseToWakes.wait}
              onChange={handleChange}
            />
            Wait
          </CheckboxLabel>
          <CheckboxLabel checked={formData.responseToWakes.other}>
            <input
              type="checkbox"
              name="responseToWakes.other"
              checked={formData.responseToWakes.other}
              onChange={handleChange}
            />
            Other
          </CheckboxLabel>
        </CheckboxGroup>
        {errors.responseToWakes && <div className="error-message">{errors.responseToWakes}</div>}
        
        {formData.responseToWakes.other && (
          <div className="mt-2">
            <input
              type="text"
              className={`form-control ${errors.responseToWakesOther ? 'is-invalid' : ''}`}
              name="responseToWakesOther"
              value={formData.responseToWakesOther}
              onChange={handleChange}
              placeholder="Please specify"
            />
            {errors.responseToWakesOther && <div className="error-message">{errors.responseToWakesOther}</div>}
          </div>
        )}
      </FormGroup>
    </FormSection>
  );
};

export default FeedingSection;
